import { useMemo, useState } from "react"
import { getJson, postJson } from "@/lib/api"
import { captureEvent } from "@/lib/analytics"
import { cn } from "@/lib/cn"
import type { FeedRunAnalysisPayload, ResearchAgentRunPayload } from "@/types/researchAgents"
import { FeedAgentAssignments } from "@/components/FeedAgentAssignments"

export function FeedRunAnalysisPanel({ payload }: { payload: FeedRunAnalysisPayload }) {
  const defaultAgentId = payload.assignments[0]?.researchAgentId ?? payload.agents[0]?.id ?? ""
  const [agentId, setAgentId] = useState<number | "">(defaultAgentId)
  const [runs, setRuns] = useState(payload.runs)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const assignmentPayload = useMemo(
    () =>
      payload.assignPath
        ? {
            feedId: payload.feedId ?? payload.feedRunId,
            agents: payload.agents,
            assignments: payload.assignments,
            assignPath: payload.assignPath,
            canManageAssignments: payload.canManageAssignments,
            limits: payload.limits ?? null,
          }
        : null,
    [payload]
  )

  async function runAgent() {
    if (agentId === "" || !payload.canRun) return
    setBusy(true)
    setError(null)
    try {
      const created = await postJson<ResearchAgentRunPayload>(payload.createRunPath, {
        research_agent_id: agentId,
        source_type: "FeedRun",
        source_id: payload.feedRunId,
      })
      setRuns((current) => [created, ...current])
      captureEvent("research_agent_run_started", {
        feed_run_id: payload.feedRunId,
        research_agent_id: agentId,
      })
      void pollRun(created.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start that analysis.")
    } finally {
      setBusy(false)
    }
  }

  async function pollRun(id: number) {
    for (let attempt = 0; attempt < 20; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000))
      try {
        const latest = await getJson<ResearchAgentRunPayload>(`/api/research_agent_runs/${id}`)
        setRuns((current) => current.map((run) => (run.id === id ? latest : run)))
        if (latest.status === "completed" || latest.status === "failed") return
      } catch {
        return
      }
    }
  }

  return (
    <div id="research-agents" className="space-y-6">
      {payload.youtube ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-3 text-2xl font-bold">Video</h2>
          <div className="flex gap-4">
            {payload.youtube.thumbnailUrl ? (
              <img src={payload.youtube.thumbnailUrl} alt="" className="h-24 w-40 flex-shrink-0 rounded object-cover" />
            ) : null}
            <div>
              <h3 className="font-semibold">
                {payload.youtube.videoUrl ? (
                  <a href={payload.youtube.videoUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                    {payload.youtube.title || "YouTube video"}
                  </a>
                ) : (
                  payload.youtube.title || "YouTube video"
                )}
              </h3>
              {payload.youtube.summary ? <p className="mt-2 text-sm text-gray-700">{payload.youtube.summary}</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="mb-2 text-2xl font-bold">Analysis tools</h2>
        <p className="mb-4 text-sm text-gray-600">
          Run a research persona against this item. House agents share the same graph and source tools — the persona is the lens (skeptic, grains, livestock, vol, energy, positioning).
        </p>
        {payload.canRun && payload.agents.length > 0 ? (
          <div className="mb-4 flex flex-wrap gap-2">
            <select
              className="rounded-md border-gray-300 text-sm"
              value={agentId}
              onChange={(event) => setAgentId(event.target.value ? Number(event.target.value) : "")}
            >
              {payload.agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void runAgent()}
              disabled={busy || agentId === ""}
              className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
            >
              {busy ? "Queuing…" : "Run analysis"}
            </button>
          </div>
        ) : (
          <p className="mb-4 text-sm text-gray-500">Sign in to run a research agent on this item.</p>
        )}
        {error ? <p className="mb-3 text-sm text-red-700">{error}</p> : null}
        {runs.length === 0 ? (
          <p className="text-sm text-gray-500">No persona runs on this item yet.</p>
        ) : (
          <ul className="space-y-3">
            {runs.map((run) => (
              <li key={run.id} className="rounded border border-gray-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{run.agentName}</span>
                  <span
                    className={cn(
                      "rounded px-2 py-0.5 text-xs",
                      run.status === "completed" && "bg-green-100 text-green-700",
                      run.status === "failed" && "bg-red-100 text-red-700",
                      run.status === "running" && "bg-blue-100 text-blue-700",
                      run.status === "pending" && "bg-gray-100 text-gray-700"
                    )}
                  >
                    {run.status}
                  </span>
                </div>
                {run.summary ? <p className="mt-2 text-sm text-gray-700">{run.summary}</p> : null}
                {run.error ? <p className="mt-2 text-sm text-red-700">{run.error}</p> : null}
                <div className="mt-2 flex gap-3 text-sm">
                  {run.chatPath ? (
                    <a href={run.chatPath} className="text-purple-700 hover:underline">
                      Open chat
                    </a>
                  ) : null}
                  {run.reportPath ? (
                    <a href={run.reportPath} className="text-blue-700 hover:underline">
                      Read report
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {assignmentPayload ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <FeedAgentAssignments payload={assignmentPayload} />
        </div>
      ) : null}
    </div>
  )
}
